'use client';

import { c as _c } from "react/compiler-runtime";
import { Accordion } from '@common/components/molecule/Accordion';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { useSyncExternalStore } from 'react';
import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { calculateInviteReward } from '@/app/donation/_utils/inviteReward';
import { COOKIES_KEYS } from '@/constants';
import { useIsOutsideWelloChannel } from '@/hooks/useIsOutsideWelloChannel';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';
import { useAuthStore } from '@/stores/AuthStore';
import { cookies } from '@/utils/cookies';
import WandImage from './event-reward-wand.png';
const ACCORDION_VALUE = 'event-reward';
interface EventRewardCardProps {
  productNumber: string;
  donationAmount: number;
  defaultExpanded?: boolean;
  mobileTitle?: string;
  className?: string;
}
export const eventRewardQueryOptions = ({
  productNumber,
  entry,
  isLogin
}: {
  productNumber: string;
  entry: ReturnType<typeof useWebviewTypeEntry>['entry'];
  isLogin: boolean;
}) => {
  const queryOptions = isLogin ? AuthQueryOptions['get:/wello/v2/auth/home-donation-event/reward'] : UnauthQueryOptions['get:/wello/v2/allows/home-donation-event/reward'];
  return queryOptions({
    query: {
      productNumber,
      entry
    }
  });
};
export const EventRewardCard = props => {
  const $ = _c(4);
  const {
    entry,
    isEntryResolved
  } = useWebviewTypeEntry();
  const isLogin = useAuthStore(_temp);
  if (!isEntryResolved || isLogin === undefined) {
    return null;
  }
  let t0;
  if ($[0] !== entry || $[1] !== isLogin || $[2] !== props) {
    t0 = <EventRewardCardContent {...props} entry={entry} isLogin={isLogin} />;
    $[0] = entry;
    $[1] = isLogin;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const EventRewardCardContent = t0 => {
  const $ = _c(42);
  const {
    productNumber,
    donationAmount,
    defaultExpanded: t1,
    mobileTitle,
    className,
    entry,
    isLogin
  } = t0;
  const defaultExpanded = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== entry || $[1] !== isLogin || $[2] !== productNumber) {
    t2 = eventRewardQueryOptions({
      productNumber,
      entry,
      isLogin
    });
    $[0] = entry;
    $[1] = isLogin;
    $[2] = productNumber;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      ...t2,
      select: _temp2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data
  } = useSuspenseQuery(t3);
  const isOutsideWelloChannel = useIsOutsideWelloChannel();
  const myMemberCode = useAuthStore(_temp3);
  const inviteMemberCode = useSyncExternalStore(_temp4, _temp5, _temp6);
  const isInviteVisible = !!inviteMemberCode && inviteMemberCode !== myMemberCode && !isOutsideWelloChannel;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = UnauthQueryOptions["get:/wello/v2/allows/partners/reward-prices"]();
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isInviteVisible) {
    t5 = {
      ...t4,
      select: _temp7,
      enabled: isInviteVisible
    };
    $[7] = isInviteVisible;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: inviteUnitAmount
  } = useQuery(t5);
  let t6;
  if ($[9] !== donationAmount || $[10] !== inviteUnitAmount || $[11] !== isInviteVisible) {
    t6 = isInviteVisible ? calculateInviteReward({
      donationAmount,
      unitAmount: inviteUnitAmount
    }) : 0;
    $[9] = donationAmount;
    $[10] = inviteUnitAmount;
    $[11] = isInviteVisible;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const inviteAmount = t6;
  let t7;
  if ($[13] !== data?.contents) {
    t7 = data?.contents ?? [];
    $[13] = data?.contents;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== inviteAmount) {
    t8 = inviteAmount > 0 ? [{
      eventName: "\uCE5C\uAD6C\uCD08\uB300 \uC774\uBCA4\uD2B8",
      point: inviteAmount
    }] : [];
    $[15] = inviteAmount;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = [...t7, ...t8];
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const rewards = t9;
  if (rewards.length === 0) {
    return null;
  }
  let t10;
  if ($[20] !== mobileTitle) {
    t10 = mobileTitle && <h2 className="hidden font-700 text-Gray-900 typo-body2 mobile-tablet:block">{mobileTitle}</h2>;
    $[20] = mobileTitle;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== defaultExpanded) {
    t11 = defaultExpanded ? [ACCORDION_VALUE] : [];
    $[22] = defaultExpanded;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Image src={WandImage} alt="" width={20} height={20} className="size-20 shrink-0 object-contain" />;
    t13 = <span className="flex-1 font-600 text-Gray-800 typo-body4">내가 받을 수 있는 혜택</span>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  const t14 = (data?.totalRewardAmount ?? 0) + inviteAmount;
  let t15;
  if ($[26] !== t14) {
    t15 = t14.toLocaleString("ko-KR");
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15) {
    t16 = <Accordion.Trigger ellipsis={true} title={<span className="flex w-full items-center gap-4">{t12}{t13}<span className="font-600 text-Gray-black typo-body4">최대{" "}{t15}원+</span></span>} hoverFeedback="icon" className="gap-4 p-16" />;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== rewards) {
    t17 = <Accordion.Content className="px-16 pb-16 pt-0"><ul className="flex flex-col gap-12 rounded-small bg-Gray-25 p-12">{rewards.map(_temp8)}</ul></Accordion.Content>;
    $[30] = rewards;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t16 || $[33] !== t17) {
    t18 = <Accordion.Item value={ACCORDION_VALUE} className="rounded-medium border border-B-300 bg-Gray-white">{t16}{t17}</Accordion.Item>;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== className || $[36] !== t11 || $[37] !== t18) {
    t19 = <Accordion variant="separated" defaultValues={t11} className={className}>{t18}</Accordion>;
    $[35] = className;
    $[36] = t11;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t10 || $[40] !== t19) {
    t20 = <section className="flex flex-col gap-8">{t10}{t19}</section>;
    $[39] = t10;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  return t20;
};
const RewardAmount = t0 => {
  const $ = _c(14);
  const {
    point,
    naverPay
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== naverPay || $[1] !== point) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const reward = point ? {
        label: "\uC6F0\uD3EC\uC778\uD2B8",
        amount: point
      } : naverPay ? {
        label: "\uB124\uC774\uBC84\uD398\uC774",
        amount: naverPay
      } : undefined;
      if (!reward) {
        t5 = null;
        break bb0;
      }
      t3 = "flex shrink-0 items-center gap-4 whitespace-nowrap text-Gray-800 typo-caption1";
      t4 = <span className="font-400">{reward.label}</span>;
      t1 = "font-600";
      t2 = reward.amount.toLocaleString("ko-KR");
    }
    $[0] = naverPay;
    $[1] = point;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t2) {
    t6 = <span className={t1}>{t2}원</span>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t6) {
    t7 = <span className={t3}>{t4}{t6}</span>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context;
}
function _temp3(state) {
  return state.myInfo?.memberCode;
}
function _temp4(onChange) {
  cookies.addChangeListener(onChange);
  return () => cookies.removeChangeListener(onChange);
}
function _temp5() {
  return cookies.get(COOKIES_KEYS.INVITE_MEMBER_CODE);
}
function _temp6() {}
function _temp7(t0) {
  const {
    context: context_0
  } = t0;
  return context_0?.inviteeUnitAmount;
}
function _temp8(t0) {
  const {
    eventName,
    point,
    naverPay
  } = t0;
  return <li key={eventName} className="flex items-start justify-between gap-8"><span className="min-w-0 flex-1 break-words font-400 text-Gray-600 typo-caption1">{eventName}</span><RewardAmount point={point} naverPay={naverPay} /></li>;
}